<template>
  <div>
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('event_log.self') }}
          </h5>
          <a
            href="#"
            target="_blank"
            @click.prevent.stop="handleDownload"
          >{{ $t('reports.common.excel_export') }} <a-icon type="download" /></a>
        </div>
      </div>
      <div class="card-body">
        <a-table
          :data-source="data"
          :columns="columns"
          :pagination="pagination"
          :row-key="record => record.id"
          bordered
          :scroll="{x: 1280}"
          @change="handleTableChange"
        >
          <div
            slot="filterDropdown"
            slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column, _filters }"
            style="padding: 8px"
          >
            <div
              v-if="column.dataIndex === 'log_category' || column.dataIndex === 'log_code'"
            >
              <a-input-search
                size="small"
                class="mb-3"
                :placeholder="$t('common.search')"
                @change="({ target: { value } }) => handleCheckboxFilter(value, _filters, column.dataIndex)"
              />
              <a-checkbox-group
                class="d-flex flex-column mb-3"
                :style="{maxHeight: '150px', overflowY: 'scroll'}"
                :value="selectedKeys"
                @change="value => setSelectedKeys(value)"
              >
                <a-checkbox
                  v-for="filter in checkboxData[column.dataIndex]"
                  :key="filter.value"
                  class="ml-2"
                  :value="filter.value"
                >
                  {{ filter.text }}
                </a-checkbox>
              </a-checkbox-group>
            </div>
            <a-input
              v-else
              v-ant-ref="c => (searchInput = c)"
              :placeholder="`Search ${column.dataIndex}`"
              :value="selectedKeys[0]"
              style="width: 188px; margin-bottom: 8px; display: block;"
              @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
              @pressEnter="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            />
            <a-button
              type="primary"
              icon="search"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >
              {{ $t('common.search') }}
            </a-button>
            <a-button
              size="small"
              style="width: 90px"
              @click="() => handleReset(clearFilters)"
            >
              {{ $t('common.reset') }}
            </a-button>
          </div>
          <a-icon
            slot="filterIcon"
            slot-scope="filtered"
            type="search"
            :style="{ color: filtered ? '#108ee9' : undefined }"
          />

          <div
            slot="datePicker"
            slot-scope="{ confirm, clearFilters, selectedKeys, setSelectedKeys }"
            class="p-3 text-end"
            style="padding: 8px"
          >
            <a-range-picker
              class="d-block mb-3"
              @change="value => handleDatePick(value, setSelectedKeys, 'date', selectedKeys, clearFilters)"
            />
            <div class="d-flex align-items-center mb-3">
              <a-time-picker
                class="flex-grow-1 mr-2"
                :placeholder="$t('event_log.time_from')"
                format="HH:mm"
                :disabled="!selectedKeys[0]"
                @change="value => handleDatePick(value, setSelectedKeys, 'time_from', selectedKeys)"
              />
              <span> ~ </span>
              <a-time-picker
                class="flex-grow-1 ml-2"
                :placeholder="$t('event_log.time_to')"
                format="HH:mm"
                :disabled="!selectedKeys[0]"
                @change="value => handleDatePick(value, setSelectedKeys, 'time_to', selectedKeys)"
              />
            </div>
            <div class="d-flex justify-content-end">
              <a-button
                type="primary"
                icon="search"
                size="small"
                style="width: 90px; margin-right: 8px"
                @click="confirm()"
              >
                {{ $t('common.search') }}
              </a-button>
              <a-button
                size="small"
                style="width: 90px"
                @click="() => handleReset(clearFilters)"
              >
                {{ $t('common.reset') }}
              </a-button>
            </div>
          </div>

          <template
            slot="customRender"
            slot-scope="text, record, index, column"
          >
            <span v-if="searchText && searchedColumn === column.dataIndex">
              <template
                v-for="(fragment, i) in text
                  .toString()
                  .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
              >
                <mark
                  v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                  :key="i"
                  class="highlight"
                >
                  {{ fragment }}
                </mark>
                <template v-else>{{ fragment }}</template>
              </template>
            </span>
            <template v-else>
              {{ text }}
            </template>
          </template>
          <template
            slot="timestamp"
            slot-scope="text"
          >
            {{ text | dateFormatter('YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template
            slot="formattedMessage"
            slot-scope="text"
          >
            {{ $l(text) }}
          </template>
          <template
            slot="eventType"
            slot-scope="text"
          >
            <a-icon
              v-if="text==='INFO'"
              type="info-circle"
              :style="{color: '#1890ff'}"
            />
            <a-icon
              v-if="text==='WARNING'"
              type="warning"
              :style="{color: '#faad14'}"
            />
            <a-icon
              v-if="text==='ERROR'"
              type="close-circle"
              :style="{color: '#f5222d'}"
            />
          </template>
          <template
            slot="username"
            slot-scope="text"
          >
            {{ text || $t('common.not_specified') }}
          </template>
          <template
            slot="note"
            slot-scope="text"
          >
            {{ JSON.stringify(text) }}
          </template>
        </a-table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import eventLogService from '@/services/api/admin/reports/event-log-services'
import reportService from '@/services/api/admin/reports/excel-report-services'

import moment from 'moment'

export default {
  name: 'EventLog',
  components: {
  },
  data: function () {
    return {
      searchText: '',
      data: [],
      pagination: {
        total: 0,
        current: 1,
        pageSize: 20,
      },
      filters: {},
      sorting: {
        col: 'date_created',
        order: 'desc',
      },
      eventCategories: [],
      eventCodes: [],
      checkboxData: {
        log_category: [],
        log_code: [],
      },
      moment: moment,
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/user',
    }),
    columns() {
      return [
        {
          dataIndex: 'level.standardLevel',
          width: 50,
          scopedSlots: {
            customRender: 'eventType',
          },
        },
        {
          title: this.$t('event_log.timestamp'),
          dataIndex: 'date_created',
          sorter: true,
          defaultSortOrder: 'descend',
          scopedSlots: {
            customRender: 'timestamp',
            filterDropdown: 'datePicker',
          },
        },
        {
          title: this.$t('event_log.event'),
          key: 'event',
          children: [
            {
              title: this.$t('event_log.category'),
              dataIndex: 'log_category',
              filters: this.eventCategories,
              scopedSlots: { filterDropdown: 'filterDropdown' },
            },
            {
              title: this.$t('event_log.code'),
              dataIndex: 'log_code',
              filters: this.eventCodes,
              scopedSlots: { filterDropdown: 'filterDropdown' },
            },
            {
              title: this.$t('event_log.title'),
              dataIndex: 'formatted_message.description',
              scopedSlots: {
                customRender: 'formattedMessage',
              },
            },
          ],
        },
        {
          title: this.$t('event_log.user'),
          key: 'user',
          children: [
            {
              title: this.$t('event_log.ip_address'),
              dataIndex: 'remote_address',
            },
            {
              title: this.$t('event_log.login'),
              dataIndex: 'username',
              scopedSlots: {
                filterDropdown: 'filterDropdown',
                filterIcon: 'filterIcon',
                customRender: 'username',
              },
            },
          ],
        },
        {
          title: this.$t('event_log.note'),
          dataIndex: 'formatted_message.payload',
          scopedSlots: {
            customRender: 'note',
          },
        },
      ]
    },
  },
  mounted() {
    Promise.all([
      this.fetchLog(),
      this.fetchCategories(),
      this.fetchCodes(),
    ])
  },
  methods: {
    handleSearch(selectedKeys, confirm) {
      this.searchText = selectedKeys[0]
      confirm()
    },
    handleReset(clearFilters) {
      this.searchText = ''
      delete this.filters.date_from
      delete this.filters.date_to
      clearFilters()
    },
    handleTableChange (pagination, filter, sorter) {
      if (sorter.columnKey && sorter.order)
        this.sorting = {
          col: sorter.columnKey,
          order: sorter.order.replace('end', ''), // ascend -> asc | descend -> desc
        }

      Object.keys(filter).forEach(key => {
        const filterValue = filter[key]
        if (filterValue && filterValue.length > 0) {
          if (key === 'date_created') {
            this.filters.date_from = filterValue[0]
            this.filters.date_to = filterValue[1]
          } else {
            this.filters[key] = filterValue
          }
        } else
          this.filters[key] = null
      })

      this.pagination.current = pagination.current

      this.fetchLog()
    },

    async fetchLog() {
      const { data } = await eventLogService.getLog(this.pagination, this.sorting, this.filters)
      this.data = data.content
      this.pagination.total = data.pagination.total
    },

    async fetchCategories() {
      const { data } = await eventLogService.getCategories()
      this.eventCategories = data.content.map(value => ({ text: value, value }))
      this.checkboxData.log_category = this.eventCategories
    },

    async fetchCodes() {
      const { data } = await eventLogService.getCodes()
      this.eventCodes = data.content.map(value => ({ text: value, value }))
      this.checkboxData.log_code = this.eventCodes
    },

    handleCheckboxFilter(value, filters, dataIndex) {
      if (!value || value.length === 0) {
        this.checkboxData[dataIndex] = filters
        return
      }
      const regex = new RegExp(`^${value}`, 'i')
      this.checkboxData[dataIndex] = filters.filter(filter => {
        return regex.test(filter.value)
      })
    },

    handleDatePick(value, setSelectedKeys, dateType, selectedKeys, clearFilters) {
      if (dateType === 'date') {
        if (value.length === 0) {
          this.handleReset(clearFilters)
        } else {
          const from = value[0].hour(0).minute(0).second(0).millisecond(0).valueOf()
          const to = value[1].hour(23).minute(59).second(59).millisecond(999).valueOf()
          setSelectedKeys([from, to])
        }
      } else {
        const dateRange = [...selectedKeys]
        let hours
        let minutes
        if (value) {
          const index = Number(dateType !== 'time_from')
          hours = value.hour()
          minutes = value.minute()
          dateRange[index] = moment(dateRange[index]).hour(hours).minute(minutes).second(0).millisecond(0).valueOf()
        } else {
          if (dateType === 'time_from') {
            dateRange[0] = moment(dateRange[0]).hour(0).minute(0).second(0).millisecond(0).valueOf()
          } else {
            dateRange[1] = moment(dateRange[1]).hour(23).minute(59).second(59).millisecond(999).valueOf()
          }
        }

        setSelectedKeys(dateRange)
      }
    },

    handleDownload() {
      reportService.getEventLogExcel(this.pagination, this.sorting, this.filters, 'event_log')
    },
  },
}
</script>
<style scoped>
</style>
